import React, { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../shadcn-ui/drawer';
import { Button } from '../shadcn-ui/button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '../FormField';
import { toast } from 'sonner';
import { endDrying, endFermentation } from '@/network/api.service';
import { useNavigate } from 'react-router-dom';
import { endDryingSchema } from '@/schema/drying-lot-schema';
import { Loader2 } from 'lucide-react';

const EndDryingDrawer = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onClose,
  lotId,
  disabled,
  stage,
}) => {
  const [loading, setLoading] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  // Use controlled open if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(endDryingSchema),
    defaultValues: {
      drying_end_time: new Date(),
    },
  });

  const handleOpenChange = newOpen => {
    setOpen(newOpen);
    if (!newOpen && onClose) {
      onClose();
    }
    if (newOpen) {
      // Blur any focused elements in the background when drawer opens
      const activeElement = document.activeElement;
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
  };

  const onSubmit = async data => {
    setLoading(true);
    try {
      const { data: respData } = await endDrying({ data, lotId: lotId });
      if (respData?.success) {
        toast.success('Drying ended successfully');
        handleOpenChange(false);
        navigate('/');
      } else {
        toast.error('Failed to end drying');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    //
  };

  const handleAddClick = () => {
    if (onClose) {
      onClose();
    }
    setOpen(true);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button className="" onClick={handleAddClick} disabled={disabled || stage !== 'Drying'}>
          End drying
        </Button>
      </DrawerTrigger>

      <DrawerContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerHeader className="text-left">
            <DrawerTitle className="p-1">End Drying</DrawerTitle>
            <DrawerDescription className="hidden"></DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col gap-3 px-5 pb-5">
            <FormField
              datetime
              label="Drying end time"
              name="drying_end_time"
              required
              control={control}
              disabled={loading}
              errors={errors}
            />
            <FormField
              label="Drying end weight (kg)"
              name="drying_end_weight_kg"
              required
              type="number"
              inputMode="decimal"
              step="0.01"
              placeholder="Enter weight"
              register={register}
              className="bg-[#FAFAFA]"
              disabled={loading}
              errors={errors}
            />
          </div>
          <DrawerFooter className={'flex flex-row justify-end gap-2 border-t'}>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="px-10">
              {loading && <Loader2 className="animate-spin mr-2 h-4 w-4" />}
              {loading ? 'Finishing...' : 'Finish'}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
};

export default EndDryingDrawer;
